/**
 * 頁 2 — 儀表板（`ui-spec` §2 頁 2；P1-12；⭐ 取代原本嘅首頁骨架）
 *
 * 依角色顯示「今日有咩要處理」：待收貨／待出貨／本週 cbm（計費基礎）／待確認倉租
 * ＋ **⚠️ 未結算場次／缺交收證據**（`rules §七 G4`／`H4`）＋ 在場 cbm。
 *
 * 紅線
 *   · ⭐ 所有數字由後端 `_lib/dashboard.ts` 出（同週費頁／未完事項頁**同一支實作**）——
 *     前端唔准自己 count／計 cbm
 *   · 睇唔到嘅卡後端回 `null` → 呢度**唔顯示**（唔准當 0：0 同「唔關你事」係兩件事）
 *   · 警示（⚠️）一律可撳入去跟（唔准靜靜消失）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { card, errText, grid2, hint as hintStyle } from '@coeldery/ui-kit';
import StatCard from '../components/StatCard';
import PendingLists from '../components/PendingLists';
import { ApiError, api, type DashboardResponse } from '../lib/api';

const fmt6 = (n: number): string => Number(n ?? 0).toFixed(6);

type Health = 'checking' | 'ok' | 'down';

export default function Dashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<Health>('checking');

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.dashboard());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 後端健康（原本首頁嘅功能；搬入儀表板最底一行，證明 Functions ＋ D1 通） */
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch('/api/health', { credentials: 'same-origin' });
        if (alive) setHealth(res.ok ? 'ok' : 'down');
      } catch {
        if (alive) setHealth('down');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  const c = data?.cards;

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('dash.title')}</h2>
      <p style={hintStyle}>{t('dash.intro')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {data?.scoped && <p style={hintStyle}>{t('dash.scoped')}</p>}

      {/* 卡（`null` ＝ 唔關你事 → 唔顯示） */}
      <div style={{ ...grid2, marginTop: '12px' }}>
        {c && c.pendingIntake !== null && (
          <StatCard label={t('dash.pendingIntake')} value={String(c.pendingIntake)} to="/intakes" />
        )}
        {c && c.pendingOutbound !== null && (
          <StatCard label={t('dash.pendingOutbound')} value={String(c.pendingOutbound)} to="/outbound" />
        )}
        {c && c.weekCbm !== null && (
          <StatCard
            label={t('dash.weekCbm')}
            value={fmt6(c.weekCbm)}
            hint={t('dash.weekCbmHint', { week: data?.currentWeekStart ?? '' })}
            to="/weekly-fees"
          />
        )}
        {c && c.unconfirmedFees !== null && (
          <StatCard
            label={t('dash.unconfirmedFees')}
            value={String(c.unconfirmedFees)}
            tone={c.unconfirmedFees > 0 ? 'warn' : 'normal'}
            to="/weekly-fees"
          />
        )}
        {c && c.unsettledEvents !== null && (
          <StatCard
            label={t('dash.unsettledEvents')}
            value={String(c.unsettledEvents)}
            tone={c.unsettledEvents > 0 ? 'warn' : 'normal'}
            hint={t('dash.unsettledEventsHint')}
            to="/pending"
          />
        )}
        {c && c.missingChop !== null && (
          <StatCard
            label={t('dash.missingChop')}
            value={String(c.missingChop)}
            tone={c.missingChop > 0 ? 'warn' : 'normal'}
            hint={t('dash.missingChopHint')}
            to="/pending"
          />
        )}
        {c && c.onSiteCbm !== null && (
          <StatCard label={t('dash.onSiteCbm')} value={fmt6(c.onSiteCbm)} to="/inventory" />
        )}
      </div>

      {/* 警示清單（每類最多 5 條；完整版頁 17） */}
      {data && (
        <>
          <h3 style={{ margin: '18px 0 0', fontSize: '15px', fontWeight: 800 }}>
            {t('dash.pendingTitle')}
          </h3>
          <PendingLists
            unsettledEvents={data.pending.unsettledEvents}
            missingChop={data.pending.missingChop}
            onSite={data.pending.onSite}
          />
          <p style={{ marginTop: '12px' }}>
            <Link to="/pending" style={{ ...hintStyle, fontWeight: 700 }}>
              {t('dash.seeAllPending')}
            </Link>
          </p>
        </>
      )}

      {/* 系統狀態（原本首頁骨架；搬入嚟唔等於冇咗） */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 800 }}>
          {t('home.healthTitle')}
        </h3>
        {health === 'checking' && <p style={{ margin: 0 }}>{t('home.checking')}</p>}
        {health === 'ok' && (
          <p style={{ ...hintStyle, color: 'var(--color-primary)', fontWeight: 700 }}>{t('home.dbUp')}</p>
        )}
        {health === 'down' && (
          <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }}>{t('home.apiDown')}</p>
        )}
      </section>
    </>
  );
}
